import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { makeButton } from '../../../../editor/features/build/requestModels/widgets/buttonStackWidget/Button';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { PageKind } from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { useButtonHandlers } from '../../../helpers/widget-helpers/use-button-handlers';
import { CreateEditButtonFormValues } from './create-edit-button-form-helpers';
import { useContext } from 'react';
import { PathGraphContext } from '../../common/PathGraph/PathGraph';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';

type ButtonsFactoryParams = {
  formValues: CreateEditButtonFormValues;
  button: ButtonModel | undefined;
  buttonIndex: number;
};

const getButtonByType = (params: ButtonsFactoryParams) => {
  const { formValues, button, buttonIndex } = params;
  const { selectedButtonType } = formValues;

  if (selectedButtonType === ButtonType.PAGE) {
    const updatedButton = makeButton({
      type: ButtonType.PAGE,
      name: formValues.buttonName,
      sortOrder: button ? button.sortOrder : buttonIndex,
      id: button ? button.id : undefined,
      tags: formValues.tags,
      configExtendObject: {
        pageId: formValues.pageId ? Number(formValues.pageId) : undefined,
      },
    });

    return {
      ...updatedButton,
      config: {
        ...updatedButton.config,
        pageHidden: true,
      },
    };
  }

  if (selectedButtonType === ButtonType.CALL) {
    return makeButton({
      type: ButtonType.CALL,
      name: formValues.buttonName,
      sortOrder: button ? button.sortOrder : buttonIndex,
      id: button ? button.id : undefined,
      configExtendObject: {
        phoneNumber: formValues.phoneNumber,
      },
    });
  }

  if (selectedButtonType === ButtonType.URL) {
    return makeButton({
      type: ButtonType.URL,
      name: formValues.buttonName,
      sortOrder: button ? button.sortOrder : buttonIndex,
      id: button ? button.id : undefined,
      tags: formValues.tags,
      configExtendObject: {
        url: formValues.url,
        webviewHeightRatio: formValues.webviewSize,
      },
    });
  }

  if (selectedButtonType === ButtonType.HANDOVER) {
    return makeButton({
      type: ButtonType.HANDOVER,
      name: formValues.buttonName,
      sortOrder: button ? button.sortOrder : buttonIndex,
      id: button ? button.id : undefined,
      configExtendObject: {
        appId: formValues.appId,
        metadata: formValues.metadata,
      },
    });
  }
};

export const useSubmitForm = (
  widget: WidgetModel,
  chatbotId: number,
  closeButtonArea: () => void,
  newDialogueName: string,
) => {
  const buttonHandlers = useButtonHandlers();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const {
    setActiveNode,
    removeNodeAndSuccessors,
    addWidgetButtonsToGraph,
    pathGraph,
  } = useContext(PathGraphContext);
  const topLevelDialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, pathGraph?.graph() || ''),
  );

  return async (
    values: CreateEditButtonFormValues,
    button: ButtonModel | undefined,
  ) => {
    const maxSortOrder = Math.max(...widget.buttons.map(b => b.sortOrder), -1);
    const buttonIndex = button ? button.sortOrder : maxSortOrder + 1;
    const updatedButton = getButtonByType({
      formValues: values,
      button,
      buttonIndex,
    }) as ButtonModel;

    // Note: BE can change ID of the button while updating it.
    // We need to fully clean up the path due to this.
    if (updatedButton) {
      const widgetPageButtonIds = [
        ...widget.buttons
          .filter(b => b.type === ButtonType.PAGE)
          .map(b => b.id),
      ];

      if (button) {
        widgetPageButtonIds.push(button.id);
      }

      for (const buttonId of widgetPageButtonIds) {
        setActiveNode?.({
          id: buttonId,
          type: 'button',
          action: 'close',
        });
      }

      if (button) {
        removeNodeAndSuccessors?.({
          id: button.id,
          type: 'button',
          actionType: 'delete',
          parentId: widget.id,
          parentType: 'widget',
        });
      }
    }

    if (updatedButton) {
      if (
        updatedButton.type === ButtonType.PAGE &&
        !(updatedButton.config as PageButtonConfig).pageId
      ) {
        const response = await dispatch(
          pageCRUDActions.create({
            botId: chatbotId,
            name: newDialogueName,
            kind: PageKind.StartBot,
            pageGroupId: topLevelDialogue?.pageGroupId,
          }),
        );

        if (response && response.success) {
          const { page } = response;
          (updatedButton.config as PageButtonConfig).pageId = page.id;
        }
      }

      let buttonAfterUpdate: ButtonModel | undefined;

      if (button) {
        // DANGER ZONE:
        // BE changes ID of the button, at least on some udpates
        buttonAfterUpdate = await buttonHandlers.updateButton(
          widget,
          updatedButton,
        );
      } else {
        buttonAfterUpdate = await buttonHandlers.createButton(
          widget,
          updatedButton,
        );
      }

      if (buttonAfterUpdate) {
        addWidgetButtonsToGraph?.(widget.id, [buttonAfterUpdate]);

        if (
          updatedButton.type === ButtonType.PAGE &&
          (buttonAfterUpdate?.config as PageButtonConfig)?.pageId
        ) {
          setActiveNode?.({
            id: buttonAfterUpdate.id,
            type: 'button',
            dialogueId: (buttonAfterUpdate?.config as PageButtonConfig)?.pageId,
            action: 'open',
          });
        }
      }
    }

    closeButtonArea();
  };
};
